import type { HttpService } from '../App/services/http-service';
import type { DataModelService } from '../Shell/PandoraSolutionBuilderAgent/services/data-model-service';
import type { ContentWidgetService } from '../Shell/PandoraSolutionBuilderAgent/services/content-widget-service';
import type { AnyObject } from '../Types';
import { ContentWidgetViewType, NavigationItemImplementation } from '../Shell/PandoraSolutionBuilderAgent/model/enumerations';
import type { PersonalizationService } from '../App/services/personalization-service';
import type { PersonalizedStorage } from '../App/services/personalization-service.contracts';
import { utils } from '../Utils/mx-utils';
import type { UserProfileService } from '../App/services/user-profile-service';
import type { GridItemHTMLElement } from 'gridstack/dist/types';
import type { IScope } from 'angular';
import type { DashboardPageController } from './dashboard-page/dashboard-page.component';
import type { MxNavigationService } from '../Navigation/mx-navigation-service';
import type { RoutingService } from '../App/services/mx-routing-service';
import type { MxApplicationService } from '../App/services/mx-application-service';
import type { DashboardLocalizationService, Localization } from './dashboard-localization.service';
import type { LocalizationStringDTO } from '../Shell/PandoraSolutionBuilderAgent/services/mx-translation-service';
import type { DashboardFiltersService, DashboardFiltersValues, InteractiveFilterParams } from './dashboard-filters.service';
import type { SearchGroup } from '../Shell/PandoraSolutionBuilderAgent/model/search-query/search-form.contracts';
import type { InteractiveFilter } from './InteractiveFilter.model';
import { dashboardRouteName } from './dashboard.module';
import type { GroupingParams } from '../Shell/PandoraSolutionBuilderAgent/layouts/search-page/search-page.contracts';
import type { DependentFiltersGroup } from './dependentFiltersGroup';
import type { DependentFilterItem, DependentFilterParams } from './dependentFilter.model';
import type { DesignerOpener } from '../Shell/PandoraSolutionBuilderAgent/services/designer-opener-service';
import type { EventHandler } from '../Utils/mx-handler.collection';

export type WidgetInstanceScope = IScope & {
	vm: {
		page: DashboardPageController;
		id: string;
		widgetInstance: WidgetInstance;
		onWidgetEdit: () => void;
		onWidgetRemove: () => void;
		onWidgetDrilldownClick: (type: string) => void;
	};
};

export interface DashboardWidgetDTO {
	Id: string;
	Category: string;
	CategoryName: string;
	Name: string;
	Title: string;
	Description?: string;
	Image?: string;
	Template: {
		Id: string;
		WidgetType: 0;
	};
	WidgetId: string;
	MinWidth?: number;
	MinHeight?: number;
	InteractiveFilter?: boolean;
	Hidden?: boolean;
	DatasetRelated?: boolean;
}

export interface DashboardWidgetInstanceDTO {
	Id: string;
	Title: string;
	Priority?: number;
	Hidden?: boolean;
	Configuration: string;
	Customization?: string;
	Widget: string;
	Owner: string;
	NavigationItem: string;
	Width?: number;
	Height?: number;
	Localization?: Record<string, string>;
	FullLocalization?: LocalizationStringDTO[];
}

export interface WidgetCategoryDTO {
	Id: string;
	Title: string;
	Description: string;
	Image: string;
}

export interface WidgetSelectedItem {
	LegendField?: string; //StackColumnChart
	LegendFieldName?: string; //PieChart
	Value?: string;
	SeriesField?: string;
	SeriesValue?: string;
	ValueField?: number; //StackColumnChart
	ValueFieldName: number; //PieChart
}

export interface WidgetInstanceTemplateData {
	widgetTemplate: string;
	widgetViewKey: string;
	configurationTemplate: string;
	configurationViewKey: string;
	dataModelExtensions: AnyObject;
}

export interface WidgetInstanceWidgetConfigurationDataModel {
	Filters?: DependentFilterParams[];
	DynamicFilters?: InteractiveFilterParams[];
	StaticFilters?: SearchGroup;
	SearchKeyword?: string | null;
	DataQueryObjId?: string | null;
	DatasetViewObjId?: string | null;
	SearchFilters?: string[];
	GroupBy?: string | null;
	StepLength?: string | null;
	ShowTimeline?: boolean;
	SeriesColumn?: string | null;
	TypeId?: string | null;

	items?: DependentFilterItem[];

	[key: string]: unknown;
}

export interface WidgetInstanceContextDataModel {
	selectedItem?: WidgetSelectedItem;
	selectedItems?: DependentFilterItem[];
	DashboardFilters?: SearchGroup;
	KeywordFilter?: string;
	ExportMode?: boolean;
	Stretchable?: boolean;

	[key: string]: unknown;
}

export interface WidgetInstanceDataModel {
	WidgetConfiguration: WidgetInstanceWidgetConfigurationDataModel;
	Context: WidgetInstanceContextDataModel;

	[key: string]: unknown;
}

export interface WidgetInstance {
	id: string;
	title: string;
	widgetId?: string;
	widget?: DashboardWidgetDTO;
	categoryId?: string;
	icon: string;
	possibleWidgets: DashboardWidgetDTO[];
	localization: Localization;
	priority: number;
	width: number;
	height: number;
	minWidth: number;
	minHeight: number;
	hidden: boolean;
	isNew?: boolean;
	isOwner: boolean;
	isEditable: boolean;
	isDevMode: boolean;
	hasEmptyRequiredInteractiveFilter?: boolean;
	owner: string;
	el?: GridItemHTMLElement;
	scope?: WidgetInstanceScope;
	dto: Required<Omit<DashboardWidgetInstanceDTO, 'Customization' | 'Localization' | 'FullLocalization'>> & Pick<DashboardWidgetInstanceDTO, 'FullLocalization'>;
	dashboard: Dashboard;
	templateData?: WidgetInstanceTemplateData;
	dataModel: WidgetInstanceDataModel;
}

type WidgetInstancePatchMode = 'layout' | 'form' | 'publish';
const WidgetInstanceLayoutProperties = ['Priority', 'Width', 'Height', 'Hidden'];

export type WidgetInstanceConfiguration = {
	dataModel: WidgetInstanceWidgetConfigurationDataModel;
};

export interface WidgetCategory {
	id: string;
	title: string;
	image: string;
	description: string;
}

export interface DashboardFiltersChangeHandler {
	onChange: (handler: EventHandler) => void;
	unsubscribe: () => EventHandler[];
	requestChange: (options: { valueChanged?: boolean; stringValueChanged?: boolean; widgetInstances?: WidgetInstance[] }) => void;
}

export interface DashboardFilters {
	keyword: string;
	dependent?: DependentFiltersGroup;
	interactive: InteractiveFilter[];
	hasDatasetRelatedWidgets?: boolean;
	status?: string;
	values: DashboardFiltersValues;
	hasNonDefaultValues?: boolean;
	changeHandler?: DashboardFiltersChangeHandler;
}

export interface Dashboard {
	title?: string;
	description?: string;
	navigationId: string;
	widgetInstances: WidgetInstance[];
	widgets: DashboardWidgetDTO[];
	categories: WidgetCategory[];
	maxPriority: number;
	isCustomized: boolean;
	owner?: string;
	isOwner: boolean;
	filters: DashboardFilters;
	autoRefreshInterval: AutoRefreshInterval;
}

export interface WidgetPersonalization {
	height?: number;
	width?: number;
	priority?: number;
	hidden?: boolean;
}

export type AutoRefreshInterval = 0 | 30 | 60 | 120 | 240 | 1440;

export interface DashboardPersonalization {
	widgets: Record<string, WidgetPersonalization>;
	autoRefreshInterval?: AutoRefreshInterval;
}

export interface AllDashboardsPersonalization {
	dashboards: Record<string, DashboardPersonalization>;
	hideRefreshPopover: boolean;
}

export class DashboardService {
	private widgets?: DashboardWidgetDTO[];
	private categories?: WidgetCategoryDTO[];
	private personalizedStorage!: PersonalizedStorage<AllDashboardsPersonalization>;
	private datasetViewToNavigationIdCache: Record<string, string> = {};
	private readonly widgetTemplates = new Map<string, WidgetInstanceTemplateData>();

	static readonly $inject = [
		'mx.SolutionBuilderAgent.Http',
		'mx.SolutionBuilderAgent.DataModelService',
		'mx.SolutionBuilderAgent.ContentWidgetService',
		'mx.SolutionBuilderAgent.PersonalizationService',
		'mx.shell.UserProfileService',
		'mx.shell.NavigationService',
		'mx.shell.RoutingService',
		'mx.shell.AppsService',
		'mx.sb.Dashboard.DashboardLocalizationService',
		'mx.sb.Dashboard.DashboardFiltersService',
		'mx.SolutionBuilderAgent.designerOpener',
	];

	constructor(
		private readonly http: HttpService,
		private readonly dataModelService: ReturnType<typeof DataModelService>,
		private readonly contentWidgetService: ContentWidgetService,
		private readonly personalizationService: ReturnType<typeof PersonalizationService>,
		private readonly userProfileService: UserProfileService,
		private readonly navigationService: MxNavigationService,
		private readonly routingService: RoutingService,
		private readonly appsService: MxApplicationService,
		private readonly dashboardLocalizationService: DashboardLocalizationService,
		private readonly dashboardFiltersService: DashboardFiltersService,
		private readonly designerOpener: DesignerOpener
	) {
		this.personalizationService.getDashboardsStorage().then((storage) => {
			this.personalizedStorage = storage;
		});
	}

	private getPersonalization<Key extends keyof DashboardPersonalization>(navigationId: string, key: Key): DashboardPersonalization[typeof key] | undefined {
		return this.personalizedStorage.getValue('dashboards')[navigationId]?.[key];
	}

	private async savePersonalization<Key extends keyof DashboardPersonalization>(navigationId: string, key: Key, value: DashboardPersonalization[typeof key]) {
		const dashboards = this.personalizedStorage.getValue('dashboards');
		dashboards[navigationId] = dashboards[navigationId] || {};
		dashboards[navigationId][key] = value;
		await this.personalizedStorage.setValue('dashboards', dashboards);
	}

	private async reloadPersonalization() {
		await this.personalizedStorage.reload();
	}

	public async getAllCategories() {
		const categoriesDTO = this.categories || (this.categories = await this.http.get<WidgetCategoryDTO[]>('api/widgetCategory'));
		return categoriesDTO.map<WidgetCategory>((categoryDTO) => ({
			id: categoryDTO.Id,
			title: categoryDTO.Title,
			image: categoryDTO.Image,
			description: categoryDTO.Description,
		}));
	}

	async getWidgets() {
		return this.widgets || (this.widgets = await this.http.get<DashboardWidgetDTO[]>('api/widget'));
	}

	async getWidgetsAndCategories() {
		const widgets = await this.getWidgets();

		const availableCategories: Record<string, boolean> = {};

		widgets.forEach((widget) => {
			if (!widget.Hidden) {
				availableCategories[widget.Category] = true;
			}
		});

		const categoryIds = Object.keys(availableCategories);

		const categories = (await this.getAllCategories()).filter((category) => categoryIds.includes(category.id));

		return { widgets, categories };
	}

	async getDashboard(navigationId: string, loadWidgetInstances = true, initialFilterValues?: DashboardFiltersValues): Promise<Dashboard> {
		let widgetInstancesDTO = await this.http.get<DashboardWidgetInstanceDTO[]>(`api/widgetInstances/${navigationId}`);
		const personalizedWidgets = this.getPersonalization(navigationId, 'widgets');

		const { widgets, categories } = await this.getWidgetsAndCategories();
		const availableWidgetsIds = widgets.map((widget) => widget.Id);
		widgetInstancesDTO = widgetInstancesDTO.filter((widgetInstanceDTO) => availableWidgetsIds.includes(widgetInstanceDTO.Widget));

		const navItem = await this.navigationService.getNavigationItem(navigationId);
		const userId = (await this.userProfileService.get()).id;

		const dashboard: Dashboard = {
			title: navItem?.title,
			description: navItem?.description,
			navigationId,
			widgetInstances: [],
			widgets,
			categories,
			maxPriority: widgetInstancesDTO.reduce((maxPriority, widgetInstanceDTO) => Math.max(maxPriority, widgetInstanceDTO.Priority ?? 0), 0),
			isCustomized: widgetInstancesDTO.reduce((isCustomized, widgetInstanceDTO) => isCustomized || !!widgetInstanceDTO.Customization, false),
			filters: {
				interactive: [],
				values: {},
				keyword: '',
				hasDatasetRelatedWidgets: false,
			},
			owner: navItem?.owner,
			isOwner: navItem?.owner === userId,
			autoRefreshInterval: this.getAutoRefreshInterval(navigationId),
		};

		if (loadWidgetInstances) {
			dashboard.widgetInstances = (
				await Promise.all(
					widgetInstancesDTO.map(async (widgetInstanceDTO) => {
						const widgetPersonalization = personalizedWidgets?.[widgetInstanceDTO.Id];

						const localization = widgetInstanceDTO.Localization ?? {};
						delete widgetInstanceDTO.Customization;
						delete widgetInstanceDTO.Localization;

						return await this.getWidgetInstance({
							dashboard,
							dto: {
								...widgetInstanceDTO,
								Priority: widgetInstanceDTO.Priority ?? 0,
								Width: widgetInstanceDTO.Width ?? 1,
								Height: widgetInstanceDTO.Height ?? 1,
								Hidden: widgetInstanceDTO.Hidden ?? false,
							},
							widgetPersonalization,
							localization,
						});
					})
				)
			).sort((widgetInstanceA, widgetInstanceB) => widgetInstanceA.priority - widgetInstanceB.priority);

			await this.dashboardFiltersService.initDashboardFilters(dashboard, initialFilterValues);
		}
		return dashboard;
	}

	async prepareNewWidgetInstance({
		dashboard,
		widget,
		title = '',
		priority = 0,
		hidden = false,
		width = 1,
		height = 1,
		widgetConfiguration,
		localization = {
			current: {},
			fields: [],
		},
	}: {
		dashboard: Dashboard;
		widget?: DashboardWidgetDTO;
		title?: string;
		priority?: number;
		hidden?: boolean;
		width?: number;
		height?: number;
		widgetConfiguration?: AnyObject;
		localization?: Localization;
	}) {
		const { id: userId, isDevMode } = await this.userProfileService.get();
		const widgetPersonalization = isDevMode ? {} : { hidden };
		const widgetInstance = await this.getWidgetInstance({
			dashboard,
			dto: {
				Id: utils.guid(),
				Owner: userId,
				Widget: widget?.Id ?? '',
				Title: title,
				Priority: priority,
				Hidden: isDevMode ? hidden : true,
				Width: width,
				Height: height,
				NavigationItem: dashboard.navigationId,
				Configuration: this.dataModelService.serializeObject({
					dataModel: widgetConfiguration,
				}),
				FullLocalization: this.dashboardLocalizationService.copyFullLocalizationDTO(localization?.fullLocalizationDTO),
			},
			widgetPersonalization,
			localization: localization.current,
		});

		widgetInstance.isNew = true;
		return widgetInstance;
	}

	private async getWidgetTemplateData(widgetId?: string): Promise<WidgetInstanceTemplateData> {
		if (widgetId && this.widgetTemplates.has(widgetId)) {
			return this.widgetTemplates.get(widgetId) as WidgetInstanceTemplateData;
		}

		const templateData = {
			widgetTemplate: '',
			widgetViewKey: 'widget_view_not_found',
			configurationTemplate: '',
			configurationViewKey: 'configuration_view_not_found',
			dataModelExtensions: {},
		};

		if (widgetId) {
			const widgetData = await this.contentWidgetService.getHtml(widgetId);

			if (widgetData?.Template.Views) {
				const widgetView = widgetData.Template.Views.find((view) => view.Type === ContentWidgetViewType.WidgetRuntime);
				templateData.widgetTemplate = widgetView?.Template ?? '';
				templateData.widgetViewKey = widgetView?.Key || 'widget_view_not_found';

				const configurationView = widgetData.Template.Views.find((view) => view.Type === ContentWidgetViewType.WidgetConfiguration);
				templateData.configurationTemplate = configurationView?.Template ?? '';
				templateData.configurationViewKey = configurationView?.Key || 'configuration_view_not_found';

				try {
					templateData.dataModelExtensions = JSON.parse(widgetData.Template.DataModelExtensions);
				} catch (e) {
					// do nothing
				}
			}
			this.widgetTemplates.set(widgetId, templateData);
		}
		return templateData;
	}

	async getWidget(widgetId: string) {
		const { widgets } = await this.getWidgetsAndCategories();
		return widgets.find((widget) => widget.Id === widgetId);
	}

	async getWidgetCategory(widgetId: string) {
		const { categories, widgets } = await this.getWidgetsAndCategories();
		const widget = widgets.find((widget) => widget.Id === widgetId);
		return categories.find((category) => category.id === widget?.Category);
	}

	async getWidgetsOfCategory(categoryId: string) {
		const { widgets } = await this.getWidgetsAndCategories();
		return widgets.filter((widget) => widget.Category === categoryId);
	}

	async changeWidgetInWidgetInstance(widgetInstance: WidgetInstance, widgetId: string) {
		const widget = await this.getWidget(widgetId);
		if (!widget || widgetInstance.widgetId === widgetId) {
			return;
		}

		widgetInstance.widgetId = widgetId;
		widgetInstance.widget = widget;
		widgetInstance.minWidth = widget.MinWidth ?? 1;
		widgetInstance.minHeight = widget.MinHeight ?? 1;
		widgetInstance.width = Math.max(widgetInstance.width, widgetInstance.minWidth);
		widgetInstance.height = Math.max(widgetInstance.height, widgetInstance.minHeight);
		widgetInstance.icon = widget.Image || 'extension';
	}

	public async refreshWidgetInstance(widgetInstance: WidgetInstance, revertDataModel = false, exportMode = false) {
		const templateData = await this.getWidgetTemplateData(widgetInstance.widgetId);
		const savedConfiguration = this.parseWidgetInstanceConfiguration(widgetInstance.dto.Configuration);

		const dataModelData = revertDataModel ? savedConfiguration.dataModel : this.getDataModelFromWidgetInstanceConfiguration(widgetInstance);

		widgetInstance.localization.fields = this.dashboardLocalizationService.localizeDataModel(dataModelData, widgetInstance.localization.current);

		widgetInstance.dataModel = await this.dataModelService.loadDataModel(
			{ ...templateData.dataModelExtensions, widgetInstanceTitle: widgetInstance.title },
			{
				WidgetConfiguration: dataModelData,
				Context: {
					ExportMode: exportMode,
					Stretchable: widgetInstance.dataModel.Context.Stretchable,
					DashboardFilters: widgetInstance.dataModel.Context.DashboardFilters,
					KeywordFilter: widgetInstance.dataModel.Context.KeywordFilter,
					selectedItems: widgetInstance.dataModel.Context.selectedItems,
					selectedItem: widgetInstance.dataModel.Context.selectedItem,
				},
			}
		);

		widgetInstance.templateData = templateData;
	}

	async changeWidgetInstanceCategory(widgetInstance: WidgetInstance, categoryId: string) {
		widgetInstance.categoryId = categoryId;
		widgetInstance.possibleWidgets = await this.getWidgetsOfCategory(categoryId);
		const defaultWidget = widgetInstance.possibleWidgets[0];
		if (defaultWidget) {
			await this.changeWidgetInWidgetInstance(widgetInstance, defaultWidget.Id);
			await this.refreshWidgetInstance(widgetInstance, false, widgetInstance.dataModel.Context.ExportMode);
		}
	}

	private async getWidgetInstance({
		dashboard,
		dto,
		widgetPersonalization = {},
		localization,
	}: {
		dashboard: Dashboard;
		dto: WidgetInstance['dto'];
		widgetPersonalization?: WidgetPersonalization;
		localization: Localization['current'];
	}): Promise<WidgetInstance> {
		const { id: userId, isDevMode } = await this.userProfileService.get();
		const isOwner = dto.Owner === userId;
		const isEditable = isOwner || isDevMode;

		const category = dto.Widget ? await this.getWidgetCategory(dto.Widget) : undefined;

		const priority = typeof widgetPersonalization?.priority === 'number' ? widgetPersonalization.priority : dto.Priority;
		const hidden = typeof widgetPersonalization?.hidden === 'boolean' ? widgetPersonalization.hidden : dto.Hidden;

		const possibleWidgets = category ? await this.getWidgetsOfCategory(category?.id) : [];

		const preparedWidgetInstance: WidgetInstance = {
			id: dto.Id,
			isOwner,
			isEditable,
			isDevMode,
			priority,
			width: typeof widgetPersonalization?.width === 'number' ? widgetPersonalization.width : dto.Width,
			height: typeof widgetPersonalization?.height === 'number' ? widgetPersonalization.height : dto.Height,
			hidden,
			icon: 'extension',
			minWidth: 1,
			minHeight: 1,
			categoryId: category?.id,
			owner: dto.Owner,
			title: dto.Title,
			possibleWidgets,
			dto,
			dashboard,
			dataModel: {
				WidgetConfiguration: this.parseWidgetInstanceConfiguration(dto.Configuration).dataModel,
				Context: {},
				selectedItems: [],
			},
			localization: {
				current: localization,
				fields: [],
				fullLocalizationDTO: dto.FullLocalization,
			},
		};

		if (dto.Widget) {
			await this.changeWidgetInWidgetInstance(preparedWidgetInstance, dto.Widget);
		}

		return preparedWidgetInstance;
	}

	async saveDashboard(dashboard: Dashboard) {
		const personalization: DashboardPersonalization['widgets'] = {};
		const widgetsToSave: WidgetInstance[] = [];

		dashboard.widgetInstances
			.filter((widgetInstance) => !widgetInstance.isNew)
			.forEach((widgetInstance) => {
				if (widgetInstance.isOwner || widgetInstance.isDevMode) {
					if (!widgetInstance.isDevMode && widgetInstance.hidden !== widgetInstance.dto.Hidden) {
						personalization[widgetInstance.id] = {
							hidden: widgetInstance.hidden,
						};
					}

					widgetsToSave.push(widgetInstance);
				} else {
					const widgetPersonalization: WidgetPersonalization = {};

					if (widgetInstance.priority !== widgetInstance.dto.Priority) {
						widgetPersonalization.priority = widgetInstance.priority;
					}
					if (widgetInstance.hidden !== widgetInstance.dto.Hidden) {
						widgetPersonalization.hidden = widgetInstance.hidden;
					}
					if (widgetInstance.width !== widgetInstance.dto.Width) {
						widgetPersonalization.width = widgetInstance.width;
					}
					if (widgetInstance.height !== widgetInstance.dto.Height) {
						widgetPersonalization.height = widgetInstance.height;
					}

					if (Object.keys(widgetPersonalization).length > 0) {
						personalization[widgetInstance.id] = widgetPersonalization;
					}
				}
			});
		await Promise.all([
			...widgetsToSave.map((widgetInstance) => this.saveWidgetInstance(widgetInstance, 'layout')),
			this.savePersonalization(dashboard.navigationId, 'widgets', personalization),
		]);
	}

	public isWidgetInstanceNotSaved(widgetInstance: WidgetInstance, patchMode: WidgetInstancePatchMode) {
		const patch = this.getDTOPatch(widgetInstance.dto, this.getWidgetInstanceDTO(widgetInstance), patchMode);
		return Object.keys(patch).length > 0;
	}

	private getWidgetInstanceConfiguration(widgetInstance: WidgetInstance) {
		return this.dataModelService.serializeObject({
			dataModel: widgetInstance.dataModel.WidgetConfiguration ?? {},
		});
	}

	private async createWidgetInstance(widgetInstance: WidgetInstance) {
		const dto = this.getWidgetInstanceDTO(widgetInstance);
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { Id, Owner, ...payload } = dto;

		const id = await this.http.post<string>(`api/widgetInstances`, payload);
		widgetInstance.id = id;
		dto.Id = id;
		widgetInstance.isNew = false;
		widgetInstance.dto = dto;

		if (!widgetInstance.isDevMode && widgetInstance.hidden !== widgetInstance.dto.Hidden) {
			const personalization = this.getPersonalization(widgetInstance.dashboard.navigationId, 'widgets') || {};

			personalization[widgetInstance.id] = {
				hidden: widgetInstance.hidden,
			};

			await this.savePersonalization(widgetInstance.dashboard.navigationId, 'widgets', personalization);
		}
	}

	private getWidgetInstanceDTO(widgetInstance: WidgetInstance): WidgetInstance['dto'] {
		return {
			Id: widgetInstance.id,
			Title: widgetInstance.title,
			Configuration: this.getWidgetInstanceConfiguration(widgetInstance),
			Priority: widgetInstance.priority,
			Widget: widgetInstance.widgetId ?? '',
			Hidden: widgetInstance.isDevMode ? widgetInstance.hidden : widgetInstance.dto.Hidden,
			Width: widgetInstance.width,
			Height: widgetInstance.height,
			Owner: widgetInstance.owner,
			NavigationItem: widgetInstance.dashboard.navigationId,
			FullLocalization: widgetInstance.localization.fullLocalizationDTO,
		};
	}

	private parseWidgetInstanceConfiguration(configurationJSON: string) {
		let widgetConfiguration: WidgetInstanceConfiguration;
		try {
			widgetConfiguration = JSON.parse(configurationJSON);
		} catch (e) {
			widgetConfiguration = {
				dataModel: {},
			};
		}
		return widgetConfiguration;
	}

	private getDataModelFromWidgetInstanceConfiguration(widgetInstance: WidgetInstance) {
		return this.parseWidgetInstanceConfiguration(this.getWidgetInstanceConfiguration(widgetInstance)).dataModel;
	}

	private getDTOPatch(existDTO: DashboardWidgetInstanceDTO, newDTO: DashboardWidgetInstanceDTO, patchMode: WidgetInstancePatchMode): Partial<DashboardWidgetInstanceDTO> {
		return Object.entries(newDTO).reduce<Partial<DashboardWidgetInstanceDTO>>((patchDTO, [key, value]) => {
			const dtoKey = key as keyof DashboardWidgetInstanceDTO;
			const isLayoutProperty = WidgetInstanceLayoutProperties.includes(dtoKey);
			if ((!isLayoutProperty && patchMode === 'layout') || (isLayoutProperty && patchMode === 'form')) {
				return patchDTO;
			}
			if (existDTO[dtoKey] !== value) {
				patchDTO[dtoKey] = value;
			}
			return patchDTO;
		}, {});
	}

	public async saveWidgetInstance(widgetInstance: WidgetInstance, patchMode: WidgetInstancePatchMode) {
		if (!this.isWidgetInstanceNotSaved(widgetInstance, patchMode) || !(widgetInstance.isOwner || widgetInstance.isDevMode)) {
			return;
		}
		widgetInstance.localization.fields.forEach((localizationField) => localizationField.applyCurrentValue());

		if (widgetInstance.isNew) {
			await this.createWidgetInstance(widgetInstance);
		} else {
			const dto = this.getWidgetInstanceDTO(widgetInstance);
			const patchDTO = this.getDTOPatch(widgetInstance.dto, dto, patchMode);
			await this.http.patch(`api/widgetInstances/${widgetInstance.id}`, patchDTO);
			Object.assign(widgetInstance.dto, patchDTO);
		}
	}

	public async revertWidgetInstance(widgetInstance: WidgetInstance) {
		let reverted = false;
		const newSavedData = this.getWidgetInstanceDTO(widgetInstance);

		if (newSavedData.Title !== widgetInstance.dto.Title) {
			widgetInstance.title = widgetInstance.dto.Title;
			reverted = true;
		}

		const widgetChanged = widgetInstance.dto.Widget && widgetInstance.dto.Widget !== newSavedData.Widget;
		const configurationChanged = widgetInstance.dto.Configuration !== newSavedData.Configuration;

		if (widgetChanged) {
			await this.changeWidgetInWidgetInstance(widgetInstance, widgetInstance.dto.Widget);
		}

		if (configurationChanged || widgetChanged) {
			await this.refreshWidgetInstance(widgetInstance, configurationChanged, widgetInstance.dataModel.Context.ExportMode);
			reverted = true;
		}

		return reverted;
	}

	async removeWidgetInstance(id: string) {
		await this.http.remove(`api/widgetInstances/${id}`);
	}

	async moveWidgetInstance(id: string, navigationId: string) {
		await this.http.post(`api/widgetInstances/${id}/move/${navigationId}`);
	}

	async copyWidgetInstance(id: string, navigationId: string) {
		await this.http.post(`api/widgetInstances/${id}/copy/${navigationId}`);
	}

	public getDashboardNavigationItems() {
		return this.navigationService.findAllItems((item) => item.implementationType === NavigationItemImplementation.Dashboard);
	}

	public getDatasetViewNavigationId(datasetViewId: string): string | undefined {
		if (typeof this.datasetViewToNavigationIdCache[datasetViewId] !== 'string') {
			this.datasetViewToNavigationIdCache[datasetViewId] =
				this.navigationService.findItem((item) => item.contentWidgetId === datasetViewId && !item.basedOnStructure)?.id ?? '';
		}
		return this.datasetViewToNavigationIdCache[datasetViewId];
	}

	public async prepareWidgetInstanceClone(widgetInstance: WidgetInstance, dashboard: Dashboard) {
		await this.dashboardLocalizationService.loadTranslations(widgetInstance);
		const newWidgetInstance = await this.prepareNewWidgetInstance({
			...widgetInstance,
			priority: dashboard.maxPriority + 1,
			dashboard,
			widget: await this.getWidget(widgetInstance?.widgetId ?? ''),
			widgetConfiguration: this.getDataModelFromWidgetInstanceConfiguration(widgetInstance),
		});
		dashboard.maxPriority += 1;
		return newWidgetInstance;
	}

	public async cloneWidgetInstanceToDashboard(originWidgetInstance: WidgetInstance, dashboard: Dashboard): Promise<WidgetInstance | null> {
		const newWidgetInstance = await this.prepareWidgetInstanceClone(originWidgetInstance, dashboard);
		await this.refreshWidgetInstance(newWidgetInstance, true, newWidgetInstance.dataModel.Context.ExportMode);
		await this.createWidgetInstance(newWidgetInstance);
		return newWidgetInstance;
	}

	public hasDrillDown(widgetInstance: WidgetInstance): boolean {
		// percentage_by_category is configured in designer, so ignore it here
		if (this.widgetIdAlias(widgetInstance.widgetId) === 'percentage_by_category') {
			return false;
		}
		const dataQueryId = this.getWidgetInstanceDataQueryId(widgetInstance);
		return !!dataQueryId;
	}

	public getWidgetInstanceDatasetViewId(widgetInstance: WidgetInstance): string | undefined | null {
		return widgetInstance.dataModel.WidgetConfiguration?.DatasetViewObjId;
	}

	public getWidgetInstanceDataQueryId(widgetInstance: WidgetInstance): string | undefined | null {
		return widgetInstance.dataModel.WidgetConfiguration?.DataQueryObjId;
	}

	public getWidgetInstanceFilters(widgetInstance: WidgetInstance): string[] {
		return widgetInstance.dataModel.WidgetConfiguration?.SearchFilters || [];
	}

	private getWidgetSelectedItem(widgetInstance: WidgetInstance | undefined): WidgetSelectedItem | undefined {
		return widgetInstance?.dataModel?.Context?.selectedItem;
	}

	public getWidgetInstanceRecordType(widgetInstance: WidgetInstance): string | undefined | null {
		return widgetInstance.dataModel.WidgetConfiguration?.TypeId;
	}

	public getWidgetInstanceGroupBy(widgetInstance: WidgetInstance | undefined): GroupingParams {
		const selectedItem = this.getWidgetSelectedItem(widgetInstance);
		return {
			Column: widgetInstance?.dataModel?.WidgetConfiguration?.GroupBy,
			Value: selectedItem ? selectedItem.Value ?? null : undefined,
			DisplayString: selectedItem?.LegendField || selectedItem?.LegendFieldName,
			StepLength: widgetInstance?.dataModel?.WidgetConfiguration?.StepLength,
			ShowTimeline: widgetInstance?.dataModel?.WidgetConfiguration?.ShowTimeline,
			SeriesColumn: widgetInstance?.dataModel?.WidgetConfiguration?.SeriesColumn,
			SeriesValue: selectedItem?.SeriesValue,
		};
	}

	public unsetSelectedItem(widgetInstance: WidgetInstance) {
		if (widgetInstance?.dataModel?.Context?.selectedItem) {
			delete widgetInstance.dataModel.Context.selectedItem;
		}
	}

	public async dashboardLookOperation(navigationId: string) {
		await this.http.post(`api/dashboard/setLook/${navigationId}`);
		const app = this.appsService.getCurrentApplication();
		this.routingService.goToUrl(this.routingService.getRouteItemUrl({ name: 'shell', parameters: { appString: app.name } }));
	}

	public async addDashboard(parentNavigationId: string, title: string): Promise<string> {
		const payload = {
			Title: title,
			ParentId: parentNavigationId,
		};

		return await this.http.post(`api/dashboard`, payload);
	}

	public async publishDashboard(dashboard: Dashboard) {
		await this.http.post(`api/dashboard/publish/${dashboard.navigationId}`);
		await this.reloadPersonalization();
	}

	public async deleteDashboard(dashboard: Dashboard) {
		await this.http.remove(`api/dashboard/${dashboard.navigationId}`);
	}

	public async removeCustomization(dashboard: Dashboard) {
		await this.http.post(`api/dashboard/removeCustomization/${dashboard.navigationId}`);
		await this.reloadPersonalization();
	}

	public async removePersonalization(dashboard: Dashboard) {
		await this.http.post(`api/dashboard/removePersonalization/${dashboard.navigationId}`);
		await this.reloadPersonalization();
	}

	public hasPersonalization(dashboard: Dashboard) {
		const personalizedWidgets = this.getPersonalization(dashboard.navigationId, 'widgets');
		return Object.keys(personalizedWidgets || {}).length > 0;
	}

	public isRefreshPopoverHidden() {
		return this.personalizedStorage.getValue('hideRefreshPopover');
	}

	public setRefreshPopoverHidden() {
		return this.personalizedStorage.setValue('hideRefreshPopover', true);
	}

	// Gives short human aliases for long non-obvious GUIDs of known categoryId.
	// This method is useful for some spot customizations of widgets by its category.
	public categoryIdAlias(categoryId: string | undefined) {
		const categoryIdAliases: Record<string, string> = {
			'1f551dc3-986c-c120-cfd2-08dcb23233e8': 'card',
			'b06078b5-8442-c489-ab18-08dcb230aac3': 'comparison',
			'b60afd36-70b0-c531-1e10-08dcb2325114': 'composition',
			'e287fa83-dc21-c3e5-0bdb-08dcb2325fb6': 'distribution',
			'fffeea54-be52-c100-8ddc-08dcb2326ca5': 'miscellaneous',
			'9e9068a1-9fdc-c167-fb8c-08dcb231fa86': 'proportion',
			'6926a0bb-fb3a-cc27-7809-08dcb2324314': 'relationship',
		};
		return categoryIdAliases[categoryId ?? ''] || 'unknown';
	}

	// Gives short human aliases for long non-obvious GUIDs of known widgetId.
	// This method is useful for some spot customizations of widgets.
	public widgetIdAlias(widgetId: string | undefined) {
		const widgetIdAliases: Record<string, string> = {
			'48b77d5d-7f1f-cf36-b773-08dcbbd4802a': 'announcements_widget',
			'9209115c-8bf4-c8ca-7804-08dcbb17dd66': 'percentage_by_category',
			'0118c8b2-6dcd-c699-b204-08dcc059288e': 'combo_chart_widget',
			'406d5bb0-0c87-c55a-e6cf-08dcb7b306a4': 'quick_link_panel',
			'470ab272-98c0-c383-3259-08dcb2fe8f70': 'bar_chart',
			'93234405-b1e8-ce99-2cc7-08dcb7ed0e1f': 'area_chart',
			'90bf2447-17f9-c3a0-92c2-08dca5afcb18': 'pie_chart',
			'abea298a-5f25-c6d4-e581-08dcd4f759d9': 'percentage_card',
			'5df6a406-8a13-cccd-bbef-08dcc927c92b': 'dropdown_list',
			'ab3a20c8-df91-c0c7-b39e-08dcb79cef91': 'number_widget',
			'17809718-70a0-ceb9-660a-08dcb7ee678d': 'embedded_web_page',
			'fe01768b-2fb6-c6cf-dab5-08dcbc53a799': 'rich_text_widget',
			'97560a70-2a58-cfef-b676-08dcb7ebf439': 'stacked_bar_chart',
			'0e0dad86-6582-cd1f-b295-08dcb2e7fecb': 'donut_chart',
			'55d26a1e-060b-c3de-69ca-08dcb2f5ff2d': 'column_chart',
			'bf22d627-96a1-c2ba-2bef-08dcb7ead56f': 'stacked_column_chart',
			'fc3fe9ef-2fae-c94d-3587-08dcb7ecd932': 'line_chart',
			'2b7a088e-d4a8-ef11-79a5-00505690e83d': 'dataquery_list',
			'256fadae-4eb2-ef11-79a5-00505690e83d': 'value_list',
			'6be77c95-b3ba-cc65-87fa-08dcf142a1b1': 'dataset_list',
			'b9572f6b-a0ad-c002-dae3-08dcf5299824': 'activity_assignments',
			'4d4494ff-5bad-ef11-1f86-ec63d7895110': 'object_details',
			'ce50132b-9d57-cee8-119f-08dd396c0c95': 'data_query_table',
			'fd254fd2-c4b2-cc58-649e-08dd4c415a3b': 'geo_map',
		};
		return widgetIdAliases[widgetId ?? ''] || 'unknown';
	}

	public navigateToDashboard(dashboard: Dashboard) {
		this.routingService.go(dashboardRouteName, {
			navigationId: dashboard.navigationId,
			filters: this.dashboardFiltersService.getFiltersRouteParams(dashboard),
		});
	}

	public changeWidgetInstanceLayout(widgetInstance: WidgetInstance) {
		if (widgetInstance.isDevMode && widgetInstance.widgetId) {
			this.designerOpener.open('PDRContentWidgetTypeWidget', widgetInstance.widgetId);
		}
	}

	public async saveAutoRefreshInterval(dashboard: Dashboard, autoRefreshInterval: AutoRefreshInterval) {
		dashboard.autoRefreshInterval = autoRefreshInterval;
		await this.savePersonalization(dashboard.navigationId, 'autoRefreshInterval', autoRefreshInterval);
	}

	private getAutoRefreshInterval(navigationId: Dashboard['navigationId']) {
		const defaultAutoRefreshInterval: AutoRefreshInterval = 120;
		return this.getPersonalization(navigationId, 'autoRefreshInterval') ?? defaultAutoRefreshInterval;
	}
}
